/**
 * Public, merchant-facing identity for a payment backend.
 *
 * The real acquirer / PSP backends (Adyen, Stripe Connect, Tilled, …) must
 * NEVER be shown to merchants. Each backend is presented as a neutral,
 * numbered "TagadaPay Processing Network" — like airport terminals. The
 * merchant knows they are on TagadaPay and can refer to their network by
 * number ("I'm on Network 1, can I move to Network 3?") without ever
 * learning the underlying provider.
 *
 * Numbers are CANONICAL and STABLE — mirror of
 * payment-stack `src/services/signing/pdf-renderer/processing-network.ts`
 * (the source of truth used on signed contracts). Never renumber; only
 * append.
 */
export const PROCESSING_NETWORK_NUMBER: Record<string, number> = {
    adyen: 1,
    tilled: 2,
    stripe: 3,
    nuvei: 4, // reserved — deal paused/unsigned
    onyx: 6, // reserved — deal paused until sandbox go-live
}

/**
 * Full merchant-facing label, e.g. "TagadaPay Processing Network 1".
 * Falls back to the unnumbered brand when the backend is unknown (never
 * leaks a real provider name).
 */
export function processingNetworkLabel(provider: string | null | undefined): string {
    const n = provider ? PROCESSING_NETWORK_NUMBER[provider.toLowerCase()] : undefined
    return n ? `TagadaPay Processing Network ${n}` : 'TagadaPay Processing Network'
}

/**
 * Short label for tight UI spots (badges, select items), e.g. "Network 1".
 * Unknown backends fall back to plain "TagadaPay".
 */
export function processingNetworkShortLabel(provider: string | null | undefined): string {
    const n = provider ? PROCESSING_NETWORK_NUMBER[provider.toLowerCase()] : undefined
    return n ? `Network ${n}` : 'TagadaPay'
}
